import React, { useEffect, useMemo, useState } from "react";
import { getTimetableRun, listClasses, listSubjects, listStaff, getPeriodTimes, getWeekSubjectPlan, getClassSubjectsByWeek, TEACHER_DEPARTMENT } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const MAX_LOAD_PER_WEEK = 32; // ngưỡng "dạy nhiều hơn 32 tiết/tuần"

function buildSessions(pt) {
  let cur = 0;
  return (pt && pt.sessions ? pt.sessions : []).map((s) => {
    const n = (s.periods || []).length;
    const range = { key: s.key, start: cur, end: cur + n - 1 };
    cur += n;
    return range;
  });
}
function sessionOf(period, sessions) {
  return sessions.find((s) => period >= s.start && period <= s.end);
}

// "Báo cáo" — tổng hợp 5 vấn đề chất lượng của 1 bản TKB đã xếp, để soát
// nhanh không cần đọc từng lớp/GV: thiếu tiết, lớp còn trống tiết 1-4, môn liền 3-5 tiết trong 1
// buổi, GV quá tải (>32 tiết/tuần), GV lên lớp chỉ 1-2 tiết/ngày (tốn công đi
// lại), GV có khoảng nghỉ dài (>2 tiết trống liền nhau) trong ngày. Tính
// hoàn toàn từ dữ liệu bản đã xếp (run.grid + shortfallDetails đã lưu sẵn),
// không gọi lại CP-SAT.
const HappySchoolRunReport = ({ runId, weekId }) => {
  const schoolId = getActiveSchoolId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [run, setRun] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [weekPlan, setWeekPlan] = useState({});
  const [pccmByWeek, setPccmByWeek] = useState({ effective: {} });

  useEffect(() => {
    if (!schoolId || !runId) return;
    setLoading(true);
    setError("");
    Promise.all([
      getTimetableRun(schoolId, runId, { liveShortfall: true }), // thiếu tiết tính lại theo grid hiện tại (bản đã sửa tay)
      listClasses(schoolId),
      listSubjects(schoolId),
      listStaff(schoolId, { department: TEACHER_DEPARTMENT, weekId }),
      getPeriodTimes(schoolId),
      weekId ? getWeekSubjectPlan(schoolId, weekId) : Promise.resolve({}),
      // PCCM ĐÃ GỘP override riêng tuần này (classSubjectsByWeek) — bắt buộc
      // dùng cái này thay vì c.subjects (PCCM CHUNG) của listClasses ở dưới,
      // nếu không báo cáo sẽ hiện SAI tên GV cho lớp/môn nào đã đổi riêng
      // tuần này (đã gặp thực tế: 12A7 Lịch sử đổi riêng tuần này sang Hoàng
      // Thị Thủy nhưng báo cáo vẫn hiện tên GV cũ trong PCCM chung).
      weekId ? getClassSubjectsByWeek(schoolId, weekId) : Promise.resolve({ effective: {} }),
    ])
      .then(([r, c, s, st, pt, plan, csByWeek]) => {
        setRun(r);
        setClasses(c);
        setSubjects(s);
        setStaff(st);
        setSessions(buildSessions(pt));
        setWeekPlan((plan && plan.plan) || plan || {});
        setPccmByWeek(csByWeek || { effective: {} });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, runId, weekId]);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  const subjectExists = useMemo(() => new Set(subjects.map((s) => s.id)), [subjects]);
  const staffById = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s.name])), [staff]);
  // (classId, subjectId) -> cấu hình PCCM HIỆU LỰC tuần này (đã gộp override
  // riêng tuần + PCCM chung) — dùng để hiện tên GV ở mọi mục báo cáo.
  const pccmOf = useMemo(() => {
    const out = {};
    Object.entries(pccmByWeek.effective || {}).forEach(([classId, subs]) => {
      Object.entries(subs || {}).forEach(([subjectId, cfg]) => { out[`${classId}|${subjectId}`] = cfg; });
    });
    return out;
  }, [pccmByWeek]);
  const grid = (run && run.grid) || {};

  const report = useMemo(() => {
    if (!run) return null;

    // 1) Thiếu tiết — lấy thẳng từ run đã lưu, kèm tên GV chính theo PCCM.
    const shortfall = (run.shortfallDetails || [])
      .map((d) => {
        const cfg = pccmOf[`${d.classId}|${d.subjectId}`];
        return {
          className: classById[d.classId] || d.classId,
          subjectName: subjectById[d.subjectId] || d.subjectId,
          teacherName: cfg && cfg.assignedTeacherId ? (staffById[cfg.assignedTeacherId] || cfg.assignedTeacherId) : "(chưa gán GV)",
          need: d.need,
          actual: d.actual,
          thieu: d.need - d.actual,
          hasEmptySlot: d.hasEmptySlot,
        };
      })
      .sort((a, b) => b.thieu - a.thieu);

    // Gom mọi ô THẬT (môn văn hoá có subjectId hợp lệ) theo (class, subject,
    // day) và theo (teacher, day) — dùng chung cho các mục dưới.
    const byClassSubjDay = {};
    const byTeacherDay = {};
    const byTeacherSubject = {}; // teacherId -> { subjectId -> count } — dùng cho mục "GV quá tải" (kèm bộ môn)
    Object.entries(grid).forEach(([key, val]) => {
      if (!Array.isArray(val) || !val[0] || !subjectExists.has(val[0])) return;
      const [classId, d, p] = key.split("|");
      const [subjectId, teacherId] = val;
      const csKey = `${classId}|${subjectId}|${d}`;
      (byClassSubjDay[csKey] = byClassSubjDay[csKey] || []).push(Number(p));
      if (teacherId) {
        const tKey = `${teacherId}|${d}`;
        (byTeacherDay[tKey] = byTeacherDay[tKey] || []).push(Number(p));
        const bySubj = (byTeacherSubject[teacherId] = byTeacherSubject[teacherId] || {});
        bySubj[subjectId] = (bySubj[subjectId] || 0) + 1;
      }
    });

    // 2) Môn liền 3,4,5 tiết CÙNG buổi (sáng/chiều riêng, không tính liền
    // xuyên buổi trưa).
    const lienTiet = [];
    Object.entries(byClassSubjDay).forEach(([k, periods]) => {
      const [classId, subjectId, d] = k.split("|");
      const sorted = [...new Set(periods)].sort((a, b) => a - b);
      let start = sorted[0], len = 1;
      const flush = (s, l) => {
        if (l >= 3) {
          const cfg = pccmOf[`${classId}|${subjectId}`];
          lienTiet.push({
            className: classById[classId] || classId,
            subjectName: subjectById[subjectId] || subjectId,
            teacherName: cfg && cfg.assignedTeacherId ? (staffById[cfg.assignedTeacherId] || cfg.assignedTeacherId) : "(chưa gán GV)",
            day: Number(d),
            len: l,
          });
        }
      };
      for (let i = 1; i < sorted.length; i++) {
        const sameSession = sessionOf(sorted[i], sessions)?.key === sessionOf(sorted[i - 1], sessions)?.key;
        if (sorted[i] === sorted[i - 1] + 1 && sameSession) {
          len++;
        } else {
          flush(start, len);
          start = sorted[i]; len = 1;
        }
      }
      flush(start, len);
    });
    lienTiet.sort((a, b) => b.len - a.len);

    // 3) GV dạy nhiều hơn 32 tiết/tuần — kèm bộ môn, 1 dòng riêng cho mỗi
    // môn GV đó dạy (GV có thể dạy nhiều môn), sắp xếp theo BỘ MÔN trước.
    // Sinh hoạt/HĐTN(SH) KHÔNG tính vào cả danh sách liệt kê LẪN tổng số tiết
    // xét ngưỡng — luôn đúng 1 tiết/tuần, không phản ánh tải dạy thật, tính
    // vào sẽ làm sai lệch ai thực sự vượt ngưỡng.
    const sinhHoatId = subjects.find((s) => ["HĐTN(SH)", "Sinh hoạt"].includes(s.name))?.id;
    const totalByTeacher = {};
    Object.entries(byTeacherSubject).forEach(([teacherId, bySubj]) => {
      Object.entries(bySubj).forEach(([subjectId, count]) => {
        if (subjectId === sinhHoatId) return;
        totalByTeacher[teacherId] = (totalByTeacher[teacherId] || 0) + count;
      });
    });
    const overload = [];
    Object.entries(totalByTeacher)
      .filter(([, total]) => total > MAX_LOAD_PER_WEEK)
      .forEach(([teacherId, total]) => {
        const bySubj = byTeacherSubject[teacherId] || {};
        Object.entries(bySubj).forEach(([subjectId, subjCount]) => {
          if (subjectId === sinhHoatId) return;
          overload.push({
            teacherId,
            teacherName: staffById[teacherId] || teacherId,
            subjectName: subjectById[subjectId] || subjectId,
            subjCount,
            total,
          });
        });
      });
    overload.sort((a, b) => a.subjectName.localeCompare(b.subjectName, "vi") || b.total - a.total);

    // 4) GV lên lớp chỉ 1-2 tiết/1 ngày.
    const fewPeriodsDay = [];
    Object.entries(byTeacherDay).forEach(([k, periods]) => {
      const [teacherId, d] = k.split("|");
      const cnt = new Set(periods).size;
      if (cnt === 1 || cnt === 2) {
        fewPeriodsDay.push({ teacherId, teacherName: staffById[teacherId] || teacherId, day: Number(d), cnt });
      }
    });
    fewPeriodsDay.sort((a, b) => a.cnt - b.cnt || a.teacherName.localeCompare(b.teacherName, "vi"));

    // 5) GV có khoảng nghỉ dài (>2 tiết trống liền nhau) trong ngày — tính
    // khoảng cách giữa 2 tiết dạy liên tiếp (số tiết trống nằm giữa).
    const bigGaps = [];
    Object.entries(byTeacherDay).forEach(([k, periods]) => {
      const [teacherId, d] = k.split("|");
      const sorted = [...new Set(periods)].sort((a, b) => a - b);
      for (let i = 1; i < sorted.length; i++) {
        const gap = sorted[i] - sorted[i - 1] - 1;
        if (gap > 2) {
          bigGaps.push({
            teacherId, teacherName: staffById[teacherId] || teacherId, day: Number(d),
            from: sorted[i - 1], to: sorted[i], gap,
          });
        }
      }
    });
    bigGaps.sort((a, b) => b.gap - a.gap);

    // 6) GV dạy đủ 10 tiết trong 1 ngày (kín cả ngày, không nghỉ tiết nào).
    const fullDay = [];
    Object.entries(byTeacherDay).forEach(([k, periods]) => {
      const [teacherId, d] = k.split("|");
      const cnt = new Set(periods).size;
      if (cnt >= 10) {
        fullDay.push({ teacherId, teacherName: staffById[teacherId] || teacherId, day: Number(d), cnt });
      }
    });
    fullDay.sort((a, b) => b.cnt - a.cnt || a.teacherName.localeCompare(b.teacherName, "vi"));

    // 7) Số tiết đã dạy / có thể dạy theo GV — "đã dạy" lấy từ PCCM (tổng
    // periodsPerWeek các lớp-môn đang là GV chính, không phụ thuộc bản xếp
    // cụ thể này); "có thể dạy" = tổng số tiết/tuần trừ giờ đã báo bận; "dùng
    // được" = có thể dạy - đã dạy — số càng thấp (kể cả âm) càng dễ vỡ lịch,
    // soát trước khi chạy để biết ai sắp hết chỗ.
    const periodsPerDay = sessions.length ? Math.max(...sessions.map((s) => s.end)) + 1 : 10;
    const totalWeekPeriods = periodsPerDay * 6;
    const usedByTeacher = {};
    const subjectsByTeacher = {}; // teacherId -> Set(tên môn) — cho cột "Bộ môn" ở mục 7
    classes.forEach((c) => {
      // Môn + số tiết TUẦN NÀY: ưu tiên weekSubjectPlan nếu lớp có khai (VD
      // Sinh học/Công nghệ luân phiên theo tuần — tuần này học Sinh thì
      // không tính Công nghệ vào "Đã dạy", dù PCCM gốc vẫn ghi cả 2 môn).
      // GV vẫn luôn lấy từ classSubjects (plan không có thông tin GV), khớp
      // đúng logic buildSchedulerInput dùng khi xếp lịch thật.
      const planMap = weekPlan[c.id];
      const usePlan = planMap && Object.keys(planMap).length > 0;
      // PCCM HIỆU LỰC tuần này (gộp override), không dùng c.subjects (PCCM
      // chung) nữa — khớp đúng GV thật đang dạy theo bản xếp (xem pccmOf ở trên).
      const cfgBySubject = (pccmByWeek.effective || {})[c.id] || {};
      const subjectIdsThisWeek = usePlan ? Object.keys(planMap) : Object.keys(cfgBySubject);
      subjectIdsThisWeek.forEach((subjectId) => {
        const cfg = cfgBySubject[subjectId];
        if (!cfg || !cfg.assignedTeacherId) return;
        const periods = usePlan ? Number(planMap[subjectId]) || 0 : Number(cfg.periodsPerWeek) || 0;
        usedByTeacher[cfg.assignedTeacherId] = (usedByTeacher[cfg.assignedTeacherId] || 0) + periods;
        const set = (subjectsByTeacher[cfg.assignedTeacherId] = subjectsByTeacher[cfg.assignedTeacherId] || new Set());
        set.add(subjectById[subjectId] || subjectId);
      });
    });
    const busyCountOf = (busyArr) => {
      const set = new Set();
      (busyArr || []).forEach((k) => {
        const [dayStr, rest] = String(k).split("_");
        const day = Number(dayStr);
        if (Number.isNaN(day)) return;
        if (rest === "S" || rest === "C") {
          const ses = sessions[rest === "S" ? 0 : 1];
          if (ses) for (let p = ses.start; p <= ses.end; p++) set.add(`${day}_${p}`);
        } else {
          const p = Number(rest);
          if (!Number.isNaN(p)) set.add(`${day}_${p}`);
        }
      });
      return set.size;
    };
    const capacity = staff
      .map((t) => {
        const used = usedByTeacher[t.id] || 0;
        const canTeach = totalWeekPeriods - busyCountOf(t.busy);
        const subjectNames = [...(subjectsByTeacher[t.id] || [])].join(", ") || "—";
        return { teacherId: t.id, teacherName: t.name, employmentType: t.employmentType || "(chưa rõ)", subjectNames, used, canTeach, remaining: canTeach - used };
      })
      .filter((r) => r.used > 0 || r.canTeach < totalWeekPeriods); // bỏ GV chưa dạy tiết nào và chưa khai bận gì (chưa có dữ liệu)
    // Mục 7: nhóm theo Cơ hữu/Thỉnh giảng trước, trong mỗi nhóm sắp theo số
    // tiết dạy giảm dần (GV dạy nhiều nhất trong nhóm lên đầu).
    capacity.sort((a, b) => a.employmentType.localeCompare(b.employmentType, "vi") || b.used - a.used);
    // Mục 8 — cùng dữ liệu, chỉ đảo chiều (nhiều tiết rảnh lên trên) để tìm
    // nhanh GV còn dư chỗ nhận thêm lớp (ngược hẳn mục 7 vốn để soi ai sắp hết chỗ).
    const capacityDesc = [...capacity].sort((a, b) => b.remaining - a.remaining);

    // 9) Lớp còn Ô TRỐNG ở tiết 1-4 của mỗi buổi (sáng/chiều) — ô không có môn/Nghỉ/HN nào cả
    // (Nghỉ, học nghề đã được ghim kín trong grid nên không bị tính là trống). Bỏ lớp giải tán
    // và lớp không có ô nào trong bản này.
    const hasCell = new Set(Object.keys(grid).map((k) => k.split("|")[0]));
    const emptyTiet14 = [];
    [...classes]
      .filter((c) => c.status !== "giai_tan" && hasCell.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true }))
      .forEach((c) => {
        for (let d = 0; d < DAYS.length; d++) {
          sessions.slice(0, 2).forEach((ses, si) => {
            const tiets = [];
            for (let i = 0; i < 4; i++) {
              const v = grid[`${c.id}|${d}|${ses.start + i}`];
              if (!v || !v[0]) tiets.push(i + 1);
            }
            if (tiets.length) emptyTiet14.push({ classId: c.id, className: c.name, day: d, buoi: si === 0 ? "Sáng" : "Chiều", tiets });
          });
        }
      });

    return { shortfall, lienTiet, overload, fewPeriodsDay, bigGaps, fullDay, capacity, capacityDesc, emptyTiet14 };
  }, [run, classById, subjectById, subjectExists, staffById, sessions, grid, classes, staff, pccmOf, pccmByWeek, weekPlan]);

  if (!runId) return <p style={{ color: "#6b7280" }}>Chưa chọn bản xếp TKB.</p>;
  if (loading || !report) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Tổng hợp nhanh các vấn đề chất lượng của bản TKB này — không cần đọc từng lớp/GV để tự soát.
      </p>

      <Section title={`1. Thiếu tiết (${report.shortfall.length})`}>
        {report.shortfall.length === 0 ? (
          <Ok text="Không lớp/môn nào thiếu tiết." />
        ) : (
          <Table
            cols={["Lớp", "Môn", "Giáo viên", "Cần", "Có", "Thiếu", "Tình trạng"]}
            rows={report.shortfall.map((r) => [
              r.className, r.subjectName, r.teacherName, r.need, r.actual,
              <b style={{ color: "#b91c1c" }}>{r.thieu}</b>,
              r.hasEmptySlot
                ? <span style={{ color: "#b91c1c", fontWeight: 600 }}>Còn trống — thiếu thật</span>
                : <span style={{ color: "#6b7280" }}>Lớp đã kín</span>,
            ])}
          />
        )}
      </Section>

      <Section title={`2. Môn liền 3-5 tiết trong 1 buổi (${report.lienTiet.length})`}>
        {report.lienTiet.length === 0 ? (
          <Ok text="Không môn nào liền quá 2 tiết trong 1 buổi." />
        ) : (
          <Table
            cols={["Lớp", "Môn", "Giáo viên", "Ngày", "Số tiết liền"]}
            rows={report.lienTiet.map((r) => [r.className, r.subjectName, r.teacherName, DAYS[r.day], <b>{r.len}</b>])}
          />
        )}
      </Section>

      <Section title={`3. GV dạy nhiều hơn ${MAX_LOAD_PER_WEEK} tiết/tuần (${new Set(report.overload.map((r) => r.teacherId)).size})`}>
        {report.overload.length === 0 ? (
          <Ok text="Không GV nào quá tải." />
        ) : (
          <Table
            cols={["Bộ môn", "Giáo viên", "Tiết môn này", "Tổng tiết/tuần"]}
            rows={report.overload.map((r) => [r.subjectName, r.teacherName, r.subjCount, <b style={{ color: "#b91c1c" }}>{r.total}</b>])}
          />
        )}
      </Section>

      <Section title={`4. GV lên lớp chỉ 1-2 tiết trong 1 ngày (${report.fewPeriodsDay.length})`}>
        {report.fewPeriodsDay.length === 0 ? (
          <Ok text="Không có ngày nào GV chỉ lên lớp 1-2 tiết." />
        ) : (
          <Table
            cols={["Giáo viên", "Ngày", "Số tiết hôm đó"]}
            rows={report.fewPeriodsDay.map((r) => [r.teacherName, DAYS[r.day], <b>{r.cnt}</b>])}
          />
        )}
      </Section>

      <Section title={`5. GV có khoảng nghỉ >2 tiết liền nhau trong ngày (${report.bigGaps.length})`}>
        {report.bigGaps.length === 0 ? (
          <Ok text="Không GV nào bị nghỉ dài giữa các tiết." />
        ) : (
          <Table
            cols={["Giáo viên", "Ngày", "Từ tiết", "Đến tiết", "Số tiết nghỉ"]}
            rows={report.bigGaps.map((r) => [r.teacherName, DAYS[r.day], r.from + 1, r.to + 1, <b style={{ color: "#b91c1c" }}>{r.gap}</b>])}
          />
        )}
      </Section>

      <Section title={`6. GV dạy đủ 10 tiết trong 1 ngày (${report.fullDay.length})`}>
        {report.fullDay.length === 0 ? (
          <Ok text="Không GV nào dạy kín 10 tiết trong 1 ngày." />
        ) : (
          <Table
            cols={["Giáo viên", "Ngày", "Số tiết hôm đó"]}
            rows={report.fullDay.map((r) => [r.teacherName, DAYS[r.day], <b style={{ color: "#b91c1c" }}>{r.cnt}</b>])}
          />
        )}
      </Section>

      <Section title={`7. Số tiết dùng / có thể dạy theo GV (${report.capacity.length})`}>
        {report.capacity.length === 0 ? (
          <Ok text="Chưa có dữ liệu GV." />
        ) : (
          <Table
            cols={["Giáo viên", "Loại", "Bộ môn", "Đã dạy", "Có thể dạy", "Dùng được"]}
            rows={report.capacity.map((r) => [
              r.teacherName, r.employmentType, r.subjectNames, r.used, r.canTeach,
              <b style={{ color: r.remaining < 0 ? "#b91c1c" : r.remaining <= 2 ? "#b45309" : "#111827" }}>{r.remaining}</b>,
            ])}
          />
        )}
      </Section>

      <Section title={`8. Số tiết rảnh theo GV — nhiều rảnh lên trên (${report.capacityDesc.length})`}>
        {report.capacityDesc.length === 0 ? (
          <Ok text="Chưa có dữ liệu GV." />
        ) : (
          <Table
            cols={["Giáo viên", "Số tiết rảnh", "Số tiết dạy", "Tổng tiết có thể dạy"]}
            rows={report.capacityDesc.map((r) => [
              r.teacherName,
              <b>{r.remaining}</b>,
              r.used, r.canTeach,
            ])}
          />
        )}
      </Section>

      <Section title={`9. Lớp còn trống tiết 1, 2, 3, 4 trong buổi (${new Set(report.emptyTiet14.map((r) => r.classId)).size} lớp, ${report.emptyTiet14.length} buổi)`}>
        {report.emptyTiet14.length === 0 ? (
          <Ok text="Không lớp nào còn trống tiết 1-4." />
        ) : (
          <Table
            cols={["Lớp", "Thứ", "Buổi", "Tiết trống"]}
            rows={report.emptyTiet14.map((r) => [r.className, DAYS[r.day], r.buoi, <b style={{ color: "#b91c1c" }}>{r.tiets.join(", ")}</b>])}
          />
        )}
      </Section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 20 }}>
    <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>{title}</h4>
    {children}
  </div>
);
const Ok = ({ text }) => <p style={{ color: "#166534", fontWeight: 600, margin: 0 }}>✓ {text}</p>;
const Table = ({ cols, rows }) => (
  <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
    <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
      <thead>
        <tr>
          {cols.map((c) => (<th key={c} style={th}>{c}</th>))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((v, j) => (<td key={j} style={td}>{v}</td>))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };

export default HappySchoolRunReport;
